<template>
  <header 
    class="cac-header"
    role="banner">
    <button
      id="hamburgerIcon"
      ref="hamburgerMenu"
      :aria-expanded="showNavigationOptions.toString()"
      :class="{ invisible: showNavigationOptions }"
      class="hamburger-menu"
      aria-label="Hamburger menu for Civic Action Center header"
      aria-controls="mobileContainerNav"
      @click.prevent="toggleNavigationOptions()"
    >
      <icon 
        :icon-name="mobileMenu"
        view-box="0 0 24 24" />
    </button>

    <div class="container">
      <div class="row">
        <div class="cac-header__inner col-18">
          <h1>
            <router-link
              :to="{ name: homeRoute }"
              class="d-flex cac-header__logo-link"
              @click.native="trackMixpanelInteract()"
            >
              <img 
                :src="logoUrl"
                :alt="logoAlt" 
                class="header-logo" >
            </router-link>
          </h1>

          <nav 
            role="navigation"
            class="cac-nav">
            <ul class="d-flex">
              <li 
                v-for="{ routeName, title } in links"
                :key="routeName">
                <router-link
                  :to="{ name: routeName }"
                  :class="{ 'active-route': isActiveRoute(routeName) }"
                  @click.native="trackMixpanelInteract()"
                >
                  {{ title }}
                </router-link>
              </li>
            </ul>
          </nav>

          <div class="cac-header__languages">
            <ul class="d-flex">
              <li 
                class="ml-2 mr-2"
                @click.prevent="setSelectedLanguage('en')">
                <button
                  :class="{ 'active-route': isLanguageSelected('en') }"
                  class="p-0"
                >
                  English
                </button>
              </li>
              <li 
                class="ml-2"
                @click.prevent="setSelectedLanguage('es')">
                <button
                  :class="{ 'active-route': isLanguageSelected('es') }"
                  class="p-0"
                >
                  Español
                </button>
              </li>
            </ul>
          </div>

          <!-- Mobile nav -->
          <template>
            <div
              v-if="showNavigationOptions"
              id="mobileContainerNav"
              role="dialog"
              aria-model="true"
              class="cac-nav--mobile__container"
            >
              <button
                ref="closeMenu"
                :aria-expanded="showNavigationOptions.toString()"
                class="close-menu hamburger-menu"
                aria-label="Close navigation menu"
                aria-controls="mobileContainerNav"
                @click.prevent="toggleNavigationOptions()"
              >
                <icon 
                  :icon-name="closeMenu"
                  view-box="0 0 24 24" />
              </button>

              <nav role="navigation">
                <ul
                  v-for="{ routeName, title } in links"
                  :key="routeName"
                  class="cac-nav--mobile__item"
                  @click.prevent="toggleNavigationOptions()"
                >
                  <li class="cac-nav__list-item">
                    <router-link
                      :to="{ name: routeName }"
                      :class="{ 'active-route': isActiveRoute(routeName) }"
                      class="cac-nav--mobile__item-text"
                    >
                      {{ title }}
                    </router-link>
                  </li>
                </ul>
              </nav>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div
      :class="{ overlay: showNavigationOptions }"
      @click.prevent="toggleNavigationOptions()"
    />
  </header>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Routes from "Constants/campaign/civicactioncenter/routeNames";
import Icon from "StyleguideComponents/Icon.vue";
import { X, MOBILE_MENU } from "Constants/styleguide/iconNames";
import STATE from "Constants/campaign/civicactioncenter/state";
import { i18n } from "JS/i18n";

import SharedMixin from "JS/mixins/campaign/civicactioncenter/shared";

const selectedLanguageContext = `cac/${STATE.SELECTED_LANGUAGE}`;

export default {
  name: "CACHeader",
  components: {
    Icon
  },
  mixins: [SharedMixin],
  data() {
    return {
      homeRoute: Routes.HOME,
      showNavigationOptions: false,
      mobileMenu: MOBILE_MENU,
      closeMenu: X,
      defaultActionOrder: [
        {
          routeName: Routes.CANDIDATES
        },
        {
          routeName: Routes.ELECTED_OFFICIALS
        },
        {
          routeName: Routes.REGISTER_TO_VOTE
        },
        {
          routeName: Routes.VOTER_INFORMATION
        }
      ]
    };
  },
  i18n,
  computed: {
    ...mapGetters({
      selectedLanguage: selectedLanguageContext
    }),
    ...mapState(STATE.SCOPE_CAMPAIGN, {
      encodedActionOrder: STATE.ACTION_ORDER,
      logoAlt: STATE.LOGO_ALT,
      logoUrl: STATE.LOGO_URL
    }),
    links() {
      let actionLinks;
      if (this.encodedActionOrder) {
        actionLinks = JSON.parse(JSON.stringify(this.encodedActionOrder));
        actionLinks.sort((a, b) => a.position - b.position);
        actionLinks.forEach(actionLink => {
          actionLink.routeName = Routes[actionLink.key];
        });
      } else {
        actionLinks = JSON.parse(JSON.stringify(this.defaultActionOrder));
      }
      actionLinks.unshift({ routeName: Routes.HOME });
      actionLinks.forEach(actionLink => {
        actionLink.title = this.actionTitles[actionLink.routeName];
      });
      return actionLinks;
    },
    actionTitles() {
      return {
        [Routes.HOME]: this.$t("cac.app.home"),
        [Routes.CANDIDATES]: this.$t("cac.app.candidates"),
        [Routes.ELECTED_OFFICIALS]: this.$t("cac.app.elected_officials"),
        [Routes.REGISTER_TO_VOTE]: this.$t("cac.app.register_to_vote_btn"),
        [Routes.VOTER_INFORMATION]: this.$t("cac.app.voter_information")
      };
    }
  },
  watch: {
    showNavigationOptions: "handleNavigationOptions"
  },
  methods: {
    ...mapActions({
      setSelectedLanguageState: selectedLanguageContext
    }),
    handleNavigationOptions(showNavigationOptions) {
      if (!showNavigationOptions) {
        this.$nextTick(() => {
          this.$refs.hamburgerMenu.focus();
        });
        document.body.classList.remove("overflow-hidden");
        return false;
      }
      this.$nextTick(() => {
        this.$refs.closeMenu.focus();
      });
      document.body.classList.add("overflow-hidden");
      return true;
    },
    toggleNavigationOptions() {
      this.trackMixpanelInteract();
      this.showNavigationOptions = !this.showNavigationOptions;
    },
    isLanguageSelected(lang) {
      return this.selectedLanguage === lang;
    },
    isActiveRoute(routeName) {
      return (
        this.$route.name === routeName || this.isActiveParentRoute(routeName)
      );
    },
    isActiveParentRoute(routeName) {
      if (this.$route.name === Routes.OFFICIAL_PROFILE) {
        return routeName === Routes.ELECTED_OFFICIALS;
      } else if (this.$route.name === Routes.CANDIDATE_PROFILE) {
        return routeName === Routes.CANDIDATES;
      }

      return false;
    },
    setSelectedLanguage(language) {
      this.setSelectedLanguageState(language);
      this.trackMixpanelInteract();
    }
  }
};
</script>
